import { MouseEvent } from "react";
import moment from "moment";

import { IPADisplaySettingsModel } from "@/Appointments/Models/UI/IPADisplaySettingsModel";
import { IPAListModel } from "@/Appointments/Models/UI/IPAListModel";
import { Appointment } from "@/Common/Api/Appointment/Dto/Appointment";
import { Client } from "@/Common/Api/Client";
import { PhoneNumber } from "@/Common/Api/Contacts/Dto/PhoneNumber";
import { BaseReactComponent, EmptyProps } from "@/Common/Components/BaseReactComponent";
import { ButtonControl } from "@/Common/Components/Buttons/ButtonControl";
import ButtonGroup from "@/Common/Components/Buttons/ButtonGroup";
import { RefreshButton } from "@/Common/Components/Buttons/RefreshButton";
import { ClickToDialViewMode } from "@/Common/Components/CallCenter/ClickToDial.types";
import { ClickToDialViewPhoneNumber } from "@/Common/Components/CallCenter/ClickToDialViewPhoneNumber";
import { TextboxControl } from "@/Common/Components/Inputs/TextboxControl";
import { PreferredPharmacyStatus } from "@/Common/Components/Patient/PreferredPharmacyStatus";
import { InvitationPopup } from "@/Common/Components/Portal/InvitationPopup/InvitationPopup";
import { PortalStatusButton } from "@/Common/Components/Portal/PortalStatusIcon";
import { QuestionnairePopupController } from "@/Common/Components/Questionnaire/QuestionnairePopupController";
import { SettingsButton } from "@/Common/Components/Settings/SettingsButton";
import { MyleTable } from "@/Common/Components/Tables/MyleTable";
import { MyleTableColumn, MyleTableColumnOptions } from "@/Common/Components/Tables/MyleTableColumn";
import { MyleTableColumnOrderControlv2 } from "@/Common/Components/Tables/MyleTableColumnOrderControlv2";
import { Toast } from "@/Common/Components/Toast/Toast";
import { MyleHelper } from "@/Common/Helpers/MyleHelper";
import { NameHelper } from "@/Common/Helpers/NameHelper";
import { PortalHelper } from "@/Common/Helpers/PortalHelper";
import { EnumAppointmentSource } from "@/Common/Models/Appointments/EnumAppointmentSource";
import { PhoneType } from "@/Common/Models/Contacts/Enums/PhoneType";
import { Guid } from "@/Common/Models/Guid";
import { PatientTab, ProfileTab } from "@/Common/Models/Patient/PatientEnums";
import { Person } from "@/Common/Models/Person/Person";
import { QuestionnaireStatusInfo } from "@/Common/Models/Questionnaires/QuestionnaireStatusInfo";
import { ColumnConfigurationModel } from "@/Common/Models/UI/Tables/ColumnConfigurationModel";
import { ITableRenderOptions } from "@/Common/Models/UI/Tables/ITableRenderOptions";
import { MyleTableTarget } from "@/Common/Models/UI/Tables/MyleTableTarget";
import * as LocalOperations from "@/Common/Operations/operations.local";
import * as LocalPatientOperations from "@/Common/Operations/operations.patient.local";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";
import { ContactDirectoryClient } from "@/ContactDirectory/Api/ContactDirectoryClient";
import { DirectoryEntityType, DirectoryPharmacySearchDto } from "@/ContactDirectory/Api/medfar-contactdirectory/dist";
import { PhoneNumberHelper } from "@/ContactDirectory/Helpers/PhoneNumberHelper";
import { DirectoryPharmacy } from "@/ContactDirectory/Models/DirectoryPharmacy";
import { IconBadge } from "@/DesignSystem";
import { PAListSettingsContainer } from "./PAListSettingsContainer";

import "./PAList.css";

class PAListState {
    public PAListModel: IPAListModel;
    public PADisplaySettingsModel: IPADisplaySettingsModel;
    public ColumnManagerModel: Immutable.List<ColumnConfigurationModel>;
    public TableColumnInfo: Immutable.List<MyleTableColumnOptions>;
    public TableRenderOptions: ITableRenderOptions;

    public hasOverlay: boolean;
    public overlaysHaveBackground: boolean;

    public selectedQuestionnaire: QuestionnaireStatusInfo;
    public appointmentButtonLoading: Set<string>;

    public activePerson: Person | null;
    public invitationPopupOpen: boolean;

    constructor(fields?: Partial<PAListState>) {
        Object.assign(this, fields);
    }
}

class PAList extends BaseReactComponent<EmptyProps, PAListState> {
    private helper: MyleHelper = new MyleHelper();
    private changeListenerMethodId: string;
    private overlayListenerMethodId: string;
    private cachedDirectoryPharmacies: Record<string, Readonly<{ directoryPharmacy: DirectoryPharmacy, cachedTimestampAt: number }>> = {};   // A record by a directory pharmacy Id.
    private readonly cacheAgeInMs: number = 5 * 60 * 1000;   // 5 minutes.

    constructor(props: EmptyProps) {
        super(props);

        this.state = new PAListState({
            TableColumnInfo: paListStore.TableColumnInfo,
            TableRenderOptions: paListStore.TableRenderOptions,
            ColumnManagerModel: paListStore.ColumnManagerModel,
            PAListModel: paListStore.PAListModel,
            PADisplaySettingsModel: paListStore.PADisplaySettingsModel,
            appointmentButtonLoading: new Set<string>(),
            invitationPopupOpen: false,
            activePerson: null
        });
    }

    public componentDidMount(): void {
        $(window).trigger("resize");

        this.changeListenerMethodId = paListStore.AddChangeListener(this.changeListener, this, "palist-listener");
        this.overlayListenerMethodId = overlayStore.AddChangeListener(this.overlayListener, this, "palist-listener");

        window.setTimeout(() => LocalEventDispatcher.dispatch(new LocalEvent(LocalOperations.Calendar.Calendar_PAList_Mounted)), 50);
    }

    public shouldComponentUpdate(nextProps: EmptyProps, nextState: PAListState): boolean {
        return (
            this.state.PAListModel !== nextState.PAListModel ||
            this.state.PAListModel.get("TableData").equals(nextState.PAListModel.get("TableData")) ||
            this.state.PADisplaySettingsModel !== nextState.PADisplaySettingsModel ||
            this.state.TableColumnInfo !== nextState.TableColumnInfo ||
            this.state.ColumnManagerModel !== nextState.ColumnManagerModel ||
            this.state.hasOverlay !== nextState.hasOverlay ||
            this.state.overlaysHaveBackground !== nextState.overlaysHaveBackground ||
            this.state.selectedQuestionnaire !== nextState.selectedQuestionnaire ||
            this.state.invitationPopupOpen !== nextState.invitationPopupOpen ||
            this.state.activePerson !== nextState.activePerson
        );
    }

    public componentDidUpdate(_: unknown, prevState: PAListState): void {
        $(window).trigger("resize");

        const currentState = this.state;
        if (this.shouldLoadPatientPreferredPharmacy(prevState, currentState)) {
            this.loadPatientPreferredPharmacy();
        }
    }

    public componentWillUnmount(): void {
        paListStore.RemoveChangeListener(this.changeListenerMethodId);
        overlayStore.RemoveChangeListener(this.overlayListenerMethodId);
    }

    public render(): JSX.Element {
        const enableInvitationFlow = cacheStore.ClinicSettings?.EnablePortalInvitationFlowV2;
        const saveoptionkey = "PaListGridSetting";

        const columnsOptions = this.moveNonFixedColumnOptions(
            this.helper.loadTableColumnOptionsFromSettings(
                saveoptionkey,
                paListStore.DefineInitialColumnsForTable() /* Default Options */
            ).toArray()
        ).toImmutable();

        const tableColumns = this.getTableColumns(columnsOptions);
        const tableRenderOptions = this.loadTableRenderOptions(columnsOptions);

        const cfgm = this.helper.castColumnConfigurationToMyleTableColumnOptions(columnsOptions.toArray());
        const callCenterFeatureFlag: boolean = cacheStore.ClinicSettings.IsCallCenterEnabled();
        return (
            <div className="pa-list-main">
                <div className="global-container">
                    <div className="main-container">
                        {this.state.hasOverlay && (
                            <div onClick={() => this.handleOverlayClick()} className={"global-overlay" + (this.state.overlaysHaveBackground ? " translucent" : "")}>
                                <div className="exitOverlay" onClick={() => overlayStore.hideAllOverlays()}>
                                    X
                                </div>
                            </div>
                        )}
                        {this.state.selectedQuestionnaire && (
                            <QuestionnairePopupController
                                questionnaire={this.state.selectedQuestionnaire}
                                onClose={() => this.mutateState(s => s.selectedQuestionnaire = null)}
                                onSubmitted={() => this.mutateState(s => s.selectedQuestionnaire = null, () => paListStore.PAListActions.LoadPAList())}
                            />
                        )}
                        <aside className="transition palist closed">
                            <div id="toggleHandle" className="handle" />
                            <div className="hide-overflow clearfix" />
                        </aside>
                        <div className="left-nav-menu palist closed" />
                        <div className="sub-container">
                            <div className="workspace">
                                <div className="myle-page-header">
                                    <div className="row">
                                        <div className="col-lg-4 col-md-4 col-sm-4 hidden-sm-down">
                                            <p className="header-font-color">{cacheStore.GetString("CalendarMenuLabel")}</p>
                                            <h3 className="thin-font">{cacheStore.GetString("PAListLabel") + " - " + moment().locale(sessionStore.userLanguageCode).format("dddd, LL")}</h3>
                                        </div>

                                        <div className="col-lg-8 col-md-8 col-sm-8">
                                            <div className="clearfix" style={{ marginTop: "20px" }}>
                                                <div className="pull-right clearfix">
                                                    <div className="pull-right">
                                                        <TextboxControl
                                                            debounceMs={400}
                                                            value={this.state.PAListModel.get("GlobalPAListSearchText")}
                                                            operation={LocalOperations.Calendar.PAList_Search_TextBoxChanged}
                                                            style={{ marginRight: "10px", width: "350px" }}
                                                            className="form-control"
                                                            placeholder={cacheStore.GetString("SearchLabel")}
                                                        />
                                                    </div>
                                                    <div className="pull-right">
                                                        <SettingsButton rightMargin={13} onClickOperation={LocalOperations.Calendar.PAList_Setting_Click} />
                                                    </div>
                                                    <div className="pull-right">
                                                        <RefreshButton className="" rightMargin={5} onClickOperation={LocalOperations.Calendar.PAList_Setting_OkClick} />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div id="posref" className="tb ws-scrollable">
                                    {this.state.PAListModel.get("ShowPAListDisplaySettings") && (
                                        <PAListSettingsContainer
                                            className="filter-component filter-component-responsive pa-list-settings"
                                            Model={this.state.PAListModel}
                                            PADisplaySettingsModel={this.state.PADisplaySettingsModel}
                                        />
                                    )}
                                    <div className="myle-doc-tb main-content table-wrapper">
                                        <MyleTable
                                            CssClass="palist"
                                            TableRenderOptions={tableRenderOptions}
                                            TableId="palist_table"
                                            TableDataCy="palist-table"
                                            Columns={tableColumns}
                                            DataSource={this.state.PAListModel.get("TableData")}
                                            ColumnSortTarget={MyleTableTarget.Myle_Calendar_PAList_SearchResults_ColumnSortClick}
                                            ColumnsPreferencesChangedOperation={ServerOperations.Server.Myle_UpdateUserSettings}
                                            RowClickOperation={LocalOperations.Calendar.PAList_RowClick}
                                            SaveOptionskeyTarget={saveoptionkey}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                {this.state.PAListModel.get("ColumnOrderingPopupOpen") && (
                    <MyleTableColumnOrderControlv2
                        TableColumns={cfgm}
                        SaveOptionskeyTarget={saveoptionkey}
                        ContainerId="columnSettingsPopup"
                        CloseColumOrder={LocalOperations.Calendar.PAList_ColumnOrderingPopup_Close}
                    />
                )}
                {enableInvitationFlow && this.state.invitationPopupOpen && (
                    <InvitationPopup
                        person={this.state.activePerson}
                        onCancel={() => this.closeInvitationPopup()}
                        onRedirect={() => this.handleRedirect()}
                    />
                )}
                {callCenterFeatureFlag && (<Toast />)}
            </div>
        );
    }

    private getTableColumns(columnsOptions: Immutable.List<MyleTableColumnOptions>): Immutable.List<MyleTableColumn> {
        return MyleTableColumn.buildList<Appointment>(
            columnsOptions,
            column => {
                switch (column.FieldName) {
                    case "StartTime":
                        return {
                            renderCell: appointment => (
                                <div>{moment(appointment.StartTime).format("HH:mm")}</div>
                            )
                        };

                    case "PatientFirstName":
                        return {
                            renderCell: appointment => (
                                <a onClick={() => this.onPatientClickCallback(appointment)}>{NameHelper.formatFullNameForHasPatientName(appointment)}</a>
                            )
                        };

                    case "PatientNAM":
                        return {
                            renderCell: appointment => (
                                <a onClick={() => this.onPatientClickCallback(appointment)} className={(appointment.PatientIsNAMExpired ? "red-text" : "")}>{appointment.PatientNAM}</a>
                            )
                        };

                    case "EmployeeFullName":
                        return {
                            renderCell: appointment => (
                                <div>{appointment.EmployeeFullName}</div>
                            )
                        };

                    case "PatientIdentifier1":
                        return {
                            renderCell: appointment => (
                                <div>{appointment.PatientIdentifier1}</div>
                            )
                        };

                    case "PatientPhoneNumbersDisplay":
                        return {
                            renderCell: appointment => this.renderPatientPhoneColumn(appointment)
                        };

                    case "PatientEmailsDisplay":
                        return {
                            renderCell: appointment => (
                                <>{appointment.PatientEmails?.map(p => (<div key={p.Address}>{p.Address}</div>))}</>
                            )
                        };

                    case "PatientAddressesDisplay":
                        column.DefaultWrapBehavior = true;
                        return {
                            renderCell: appointment => (
                                <div>{appointment.PatientAddressesDisplay}</div>
                            )
                        };

                    case "PreferredPharmacy":
                        return {
                            renderCell: appointment => (
                                <PreferredPharmacyStatus
                                    customPharmacy={appointment.PatientPreferredPharmacy}
                                    directoryPharmacyId={appointment.PatientDirectoryPharmacyId}
                                    directoryPharmacy={appointment.PatientDirectoryPharmacy}
                                    skipToLoadDirectoryPharmacy
                                />
                            )
                        };

                    case "Subject":
                        return {
                            renderCell: appointment => {
                                let subjectFull = appointment.Subject;

                                if (appointment.ServiceDefinitionId)
                                    subjectFull = `${cacheStore.GetServiceDefinitionName(appointment.ServiceDefinitionId)} - ${subjectFull}`;

                                return (<div>{subjectFull}</div>);
                            }
                        };

                    case "SiteName":
                        return {
                            renderCell: appointment => {
                                const site = cacheStore.GetSiteById(appointment.SiteId);
                                return <div>{site.Model.ClinicalName}</div>;
                            }
                        };

                    case "Questionnaire":
                        return {
                            renderCell: appointment => (
                                <div>{this.renderQuestionnaireInfo(appointment)}</div>
                            )
                        };

                    case "AppointmentStateLookupName":
                        return {
                            renderCell: appointment => {
                                if (!cacheStore.ClinicSettings.IsCalendarReadOnly && !appointment.IsReadOnly) {
                                    if (["UNCONFIRMED", "CONFIRMED", "MESSAGE LEFT"].contains(appointment.AppointmentStateLookupCode)) {
                                        return (
                                            <ButtonControl
                                                onClick={() => this.handleOnChangeStatusAppointment(appointment)}
                                                variant="brand"
                                                disabled={this.state.appointmentButtonLoading[appointment.AppointmentId] ?? false}
                                                label={cacheStore.GetString("ToWaitingRoomLabel")}
                                            />
                                        );
                                    }
                                }

                                return <div> {appointment.AppointmentStateLookupName} </div>;
                            }
                        };

                    case "Portal":
                        return {
                            renderCell: appointment => {
                                const portalButtonProps = cacheStore.ClinicSettings.EnablePortalInvitationFlowV2
                                    ? PortalHelper.getPortalButtonPropsForRegistrationState(appointment.PatientIndividual?.PortalSyncState, true)
                                    : null;

                                return (
                                    <PortalStatusButton
                                        style={{ height: 33, width: 33 }}
                                        onClick={e => this.openInvitationPopup(e, appointment.PatientContactId)}
                                        {...portalButtonProps}
                                    />
                                );
                            }
                        };

                    case "PrintOptions":
                        return {
                            renderCell: appointment => (
                                <div>
                                    <ButtonGroup spacing="sm">
                                        <ButtonControl
                                            variant="brand"
                                            label="C1"
                                            id="c1printlabel"
                                            onClick={() => sessionStore.dispatch(
                                                LocalOperations.Calendar.PAList_LabelPrint_CompleteOne,
                                                {
                                                    item: appointment,
                                                    id: "c1printlabel"
                                                }
                                            )}
                                        />
                                        <ButtonControl
                                            variant="brand"
                                            label="$"
                                            id="$printlabel"
                                            onClick={() => sessionStore.dispatch(
                                                LocalOperations.Calendar.PAList_LabelPrint_Billing,
                                                {
                                                    item: appointment,
                                                    id: "$printlabel"
                                                }
                                            )}
                                        />
                                    </ButtonGroup>
                                </div>
                            )
                        };
                }
            },
            false
        );
    }

    private handleRedirect(): void {
        window.opener.sessionStore.dispatchAsync(LocalPatientOperations.UI.ChangeVerticalTabs, { value: PatientTab.Profile });
        window.opener.sessionStore.dispatchAsync(LocalPatientOperations.Profile.ChangeProfileTabs, { value: ProfileTab.Relationships });
        window.opener.focus();

        this.closeInvitationPopup();
    }

    private openInvitationPopup(e: MouseEvent<HTMLButtonElement>, patientContactId: Guid): void {
        e.stopPropagation();

        patientStore.setActivePatientUI(patientContactId, true, () => {
            const person = Person.FromPatient(patientStore.getCurrentPatient());

            this.mutateState(state => {
                state.invitationPopupOpen = true;
                state.activePerson = person;
            });
        });
    }

    private closeInvitationPopup(): void {
        this.mutateState(state => {
            state.invitationPopupOpen = false;
            state.activePerson = null;
        });
    }

    private handleOnChangeStatusAppointment(appointment: Appointment): void {
        if (this.state.appointmentButtonLoading.has(appointment.AppointmentId))
            return;

        this.mutateState(
            s => s.appointmentButtonLoading.add(appointment.AppointmentId),
            async () => {
                const requestData = {
                    AppointmentId: appointment.AppointmentId,
                    StateLookupId: cacheStore.GetSingleAppointmentStateLookup("COMPLETED")[0].LookupID,
                    Source: EnumAppointmentSource.Myle_PAList
                };

                await Client.postAsync(ServerOperations.Server.Myle_Appointment_ChangeStatus, requestData);

                this.mutateState(
                    s => s.appointmentButtonLoading.delete(appointment.AppointmentId),
                    () => {
                        if (!this.state.appointmentButtonLoading.size)
                            paListStore.PAListActions.LoadPAList();
                    }
                );
            }
        );
    }

    private handleOverlayClick(): void {
        overlayStore.handleOverlayClick();
        LocalEvent.Dispatch(LocalOperations.Calendar.PAList_Setting_OkClick);
    }

    private onPatientClickCallback(appointment: Appointment): void {
        patientStore.setActivePatientUI(appointment.PatientContactId, true);
    }

    private renderQuestionnaireInfo(appointment: Appointment): JSX.Element {
        if (!appointment.QuestionnaireStatusInfo)
            return null;

        let questionnaire = new QuestionnaireStatusInfo(appointment.QuestionnaireStatusInfo);
        return questionnaire.renderLink(
            q => this.mutateState(s => s.selectedQuestionnaire = q),
            appointment.StartTime,
            appointment.AppointmentStateLookupId,
            appointment.PatientId,
            appointment.IsSharedWithPortal
        );
    }

    public changeListener(): void {
        this.mutateState(state => {
            state.PAListModel = paListStore.PAListModel;
            state.PADisplaySettingsModel = paListStore.PADisplaySettingsModel;
            state.TableColumnInfo = paListStore.TableColumnInfo;
            state.ColumnManagerModel = paListStore.ColumnManagerModel;
            state.TableRenderOptions = paListStore.TableRenderOptions;
        });
    }

    public overlayListener(): void {
        this.mutateState(state => {
            state.hasOverlay = overlayStore.hasOverlay();
            state.overlaysHaveBackground = overlayStore.overlaysHaveBackground();
        });
    }

    public loadTableRenderOptions(columnOptions: Immutable.List<MyleTableColumnOptions>): ITableRenderOptions {
        // Get The Render Options
        return Immutable.fromJS({
            MinColWidth: 60,
            MinTableWidth: 980,
            Cols: columnOptions.filter(x => x.IsHidden === false).map(i => i.WidthPercentage),
            FirstColumnNoDrag: true,
            LastColumnNoDrag: true,
            Autoresize: true,
            OnColumnWidthChange: null
        });
    }

    private shouldLoadPatientPreferredPharmacy(previous: PAListState, current: PAListState): boolean {
        if (!current) {
            return false;
        }

        const getAppointments = (state: PAListState): Appointment[] => state
            ?.PAListModel
            ?.get("TableData")
            ?.toArray() ?? [];
        const previousAppointments = getAppointments(previous);
        const currentAppointments = getAppointments(current);

        if (!currentAppointments.length) {
            return false;
        }

        if (previousAppointments.length !== currentAppointments.length) {
            return true;
        }

        const previousAppointmentsDictionary = previousAppointments.toDictionary(p => p.AppointmentId);
        return previous !== current && currentAppointments.some(p => {
            const previousAppointment = previousAppointmentsDictionary[p.AppointmentId];
            return p.PatientDirectoryPharmacyId !== previousAppointment.PatientDirectoryPharmacyId
                || p.PatientDirectoryPharmacy !== previousAppointment.PatientDirectoryPharmacy;
        });
    }

    private async loadPatientPreferredPharmacy(): Promise<void> {
        const appointmentsInState = this.state.PAListModel?.get("TableData")?.toArray() ?? [];
        const directoryPharmacyIds = appointmentsInState
            .filter(p => p.PatientDirectoryPharmacyId && !p.PatientDirectoryPharmacy)
            .map(p => p.PatientDirectoryPharmacyId)
            .distinct();

        if (!directoryPharmacyIds.length) {
            return;
        }

        const currentTimestampAt = (new Date()).getTime();
        const directoryPharmacies = { ...this.cachedDirectoryPharmacies };
        const directoryPharmacyIdsToFetch = directoryPharmacyIds.filter(id =>
            !directoryPharmacies[id]
            || (currentTimestampAt - directoryPharmacies[id].cachedTimestampAt) >= this.cacheAgeInMs
        );
        if (directoryPharmacyIdsToFetch.length) {
            try {
                const entities = (
                    await ContactDirectoryClient
                        .getInstance()
                        .getByIds(DirectoryEntityType.Pharmacy, directoryPharmacyIdsToFetch) as DirectoryPharmacySearchDto[]
                );

                const timestampAt = (new Date()).getTime();
                entities.forEach(entity => {
                    if (!entity) {
                        return;
                    }

                    directoryPharmacies[entity.id] = { directoryPharmacy: DirectoryPharmacy.fromSearchDto(entity), cachedTimestampAt: timestampAt };
                });
            }
            catch {
                // No action is implemented for the failure in the api request.
            }
        }

        const newAppointments: Appointment[] = appointmentsInState
            .map(
                appointment => {
                    const { PatientDirectoryPharmacyId: directoryPharmacyId, PatientDirectoryPharmacy: directoryPharmacy } = appointment;
                    if (!directoryPharmacyId || directoryPharmacy) {
                        return appointment;
                    }

                    const foundDirectoryPharmacy = directoryPharmacies[directoryPharmacyId];
                    if (foundDirectoryPharmacy) {
                        appointment.PatientDirectoryPharmacy = foundDirectoryPharmacy.directoryPharmacy;
                    }

                    return appointment;
                }
            );
        this.mutateState(
            p => p.PAListModel = p.PAListModel.set("TableData", newAppointments.toImmutable()),
            () => this.cachedDirectoryPharmacies = directoryPharmacies
        );
    }

    private moveNonFixedColumnOptions(columnOptions: MyleTableColumnOptions[]): MyleTableColumnOptions[] {
        const firstFixedColumnIndex = columnOptions.findIndex(p => p.IsFixedInPlace);
        if (firstFixedColumnIndex === -1 || columnOptions.every((p, index) => index < firstFixedColumnIndex || p.IsFixedInPlace)) {
            return columnOptions;
        }

        // Move any non-fixed columns before the first fixed column to avoid conflicts with existing settings.
        const fixedColumns: MyleTableColumnOptions[] = [];
        const nonFixedColumnsBefore: MyleTableColumnOptions[] = [];
        const nonFixedColumnsAfter: MyleTableColumnOptions[] = [];

        for (let index = 0; index < columnOptions.length; index++) {
            const column = columnOptions[index];
            if (index < firstFixedColumnIndex) {
                nonFixedColumnsBefore.push(column);
            }
            else if (column.IsFixedInPlace) {
                fixedColumns.push(column);
            }
            else {
                nonFixedColumnsAfter.push(column);
            }
        }

        return [
            ...nonFixedColumnsBefore,
            ...nonFixedColumnsAfter,
            ...fixedColumns
        ];
    }

    /**
     * Renders the patient's phone column with formatted phone numbers, contact types,
     * and icons that indicate notification preferences.
     * @param {Appointment} appointment - Patient appointment object
     * @returns {JSX.Element} - A JSX element displaying formatted phone numbers with
     * icons and labels for each contact type.
     */
    private renderPatientPhoneColumn(appointment: Appointment): JSX.Element {
        const isAnyPhoneNotifiable = appointment.PatientPhoneNumbers?.any(p => p.NotificationsEnabled) ?? false;
        const isAnyEmailNotifiable = appointment.PatientEmails?.any(p => p.NotificationsEnabled) ?? false;

        return (
            <>
                {appointment.PatientPhoneNumbers?.map(p => {
                    const phoneNumber = PhoneNumber.stringFormattedPhone(p, true);
                    const isPhoneNotifiable = p.PhoneTypeLookupId == cacheStore.getLookupByPhoneType(PhoneType.Mobile)?.LookupID;
                    let phoneIcon = PhoneNumberHelper.getPhoneIconVariant(
                        p.NotificationsEnabled,
                        !isAnyPhoneNotifiable && isPhoneNotifiable,
                        isAnyEmailNotifiable,
                        appointment.PatientDirectoryPharmacy?.isMylePharmacy
                    );

                    const callCenterFeatureFlag: boolean = cacheStore.ClinicSettings.IsCallCenterEnabled();
                    let phoneNumberControl = <>{phoneNumber}</>;

                    if (callCenterFeatureFlag) {
                        phoneNumberControl = <ClickToDialViewPhoneNumber phoneNumber={p} viewMode={ClickToDialViewMode.Hyperlink} patientContactFacetId={appointment.PatientId} />;
                    }

                    return (
                        <div key={p.ContactPoint_id} className="palist-patient-phone-numbers">
                            <span>{phoneNumberControl}</span>
                            <span className="palist-phone-type text-gray">
                                ({MyleHelper.ReplaceEmptyString(cacheStore.GetLocalizedValueByLookupId(p.ContactPointTypeLookupId))} - {MyleHelper.ReplaceEmptyString(cacheStore.GetLocalizedValueByLookupId(p.PhoneTypeLookupId))})
                            </span>
                            {phoneIcon && (
                                <span className="palist-phone-icon" title={cacheStore.GetString(phoneIcon.tooltipLocalizationKey)}>
                                    <IconBadge size="sm" variant={phoneIcon.variant} />
                                </span>
                            )}
                        </div>
                    );
                })}
            </>
        );
    }
}

export { PAList };
